import { useMemo } from 'react'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { AskPanel, CouncilBoard } from '@/shared/ui/council'
import {
  analyticsCoverage, analyticsGaps, analyticsImpact, analyticsMoney, analyticsOverview,
  analyticsQuality,
} from '../api'
import { advise, ask, programContext, type Board } from './council'
import { AnalyticsFilterBar, useAnalyticsFilters } from './shell'

/**
 * The council over the whole portfolio.
 *
 * ## Why this screen reads six endpoints and the other twelve read one
 *
 * Each of the other screens answers one question and is right to fetch only what that
 * question needs. A decision is never about one of them: "should we keep funding this" is
 * simultaneously a question about headcount, money, measured effect, where nobody was
 * reached, and whether the record is good enough to say any of it. Splitting that across
 * twelve tabs is how a portfolio gets funded on the one screen somebody happened to open.
 *
 * The six load in parallel and each is allowed to fail on its own — a portfolio with no
 * impact rollup should still get findings about its money, and a `Promise.all` that rejects
 * as a unit would give an error page instead.
 */
export function ProgramCouncilScreen() {
  // `query`, not the export filter: the two use DIFFERENT parameter names and sending the
  // wrong set binds nothing server-side, silently answering about the whole portfolio while
  // the chips say a filter is on. See the note in `useAnalyticsFilters`.
  const { query: params } = useAnalyticsFilters()

  // `.catch(() => null)` per call, deliberately: one missing rollup must not blank the board.
  const board = useLoad<Board>(async () => {
    const [overview, money, impact, coverage, gaps, quality] = await Promise.all([
      analyticsOverview(params).catch(() => null),
      analyticsMoney(params).catch(() => null),
      analyticsImpact(params).catch(() => null),
      analyticsCoverage(params).catch(() => null),
      analyticsGaps(params).catch(() => null),
      analyticsQuality(params).catch(() => null),
    ])
    return { overview, money, impact, coverage, gaps, quality }
  }, [JSON.stringify(params)])

  const b = board.data
  const findings = useMemo(() => (b ? advise(b) : []), [b])
  const missing = b
    ? (['overview', 'money', 'impact', 'coverage', 'gaps', 'quality'] as const)
        .filter((k) => b[k] == null)
    : []

  return (
    <div className="space-y-4">
      <SectionHeader
        title="AI Council"
        description="The portfolio read as decisions — headcount, money, measured effect, where nobody was reached, and whether the record can carry any of it."
      />
      <AnalyticsFilterBar />
      <ErrorStrip message={board.error} />

      {board.loading && !b ? <LoadingBlock label="Reading the portfolio…" /> : !b ? null : (
        <>
          {missing.length > 0 && (
            <Panel>
              <p className="text-erp-sm text-muted-foreground">
                {missing.length} of the six rollups did not return ({missing.join(', ')}), so the
                findings below are drawn from the rest. That is not the same as those areas being
                clean — nothing was read about them.
              </p>
            </Panel>
          )}

          <div className="grid gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <CouncilBoard
                findings={findings}
                title="What the portfolio is saying"
                description={`${findings.length} ${findings.length === 1 ? 'finding' : 'findings'} across headcount, money, impact, coverage and record quality — ranked by the people each is worth.`}
                stakeUnit="person"
                emptyMessage="Nothing in the portfolio crosses a threshold. Widen the window if you are checking whether delivery has been slipping."
              />
            </div>
            <AskPanel
              storageKey={`programs-council-history:${JSON.stringify(params)}`}
              description="Plain language. The portfolio's figures go with the question, so the answer is about these programmes."
              placeholder="Which programmes should we keep funding?"
              scopeNote="It sees the de-duplicated headcount, the money, the measured effect and the gaps — not individual beneficiaries."
              suggestions={[
                'Which programmes should we keep funding?',
                'How many people have we actually reached?',
                'Is any of this working?',
                'Where should we expand next?',
                'What would an auditor find first?',
              ]}
              onAsk={(q) => ask(q, findings)}
              context={() => programContext(b, findings)}
            />
          </div>
        </>
      )}
    </div>
  )
}
